import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Save, X } from "lucide-react";
import { DateTime } from "luxon";
import { walkSchedulesAPI } from "../../../services/walkSchedulesAPI";

const MEETING_TYPES = [
  ["call", "Call"],
  ["site_walk", "Site walk"],
  ["inspection_review", "Inspection review"],
  ["finding_clarification", "Finding clarification"],
  ["reinspection", "Reinspection"],
];

const roleLabel = (role) =>
  String(role || "")
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const memberId = (member) => Number(member?.user_id ?? member?.id ?? member?.value);
const memberName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
  member?.displayName ||
  member?.label ||
  member?.email ||
  `User #${memberId(member)}`;

const toLocalFields = (value, zone) => {
  const dt = DateTime.fromISO(value || "", { zone: "utc" }).setZone(zone || "UTC");
  return dt.isValid
    ? { date: dt.toISODate(), time: dt.toFormat("HH:mm") }
    : { date: "", time: "" };
};

// Name A-Z, then role/email/id for deterministic duplicate-name ordering.
const compareMembers = (left, right) =>
  memberName(left).localeCompare(memberName(right), undefined, { sensitivity: "base", numeric: true }) ||
  roleLabel(left.role).localeCompare(roleLabel(right.role), undefined, { sensitivity: "base" }) ||
  String(left.email || "").localeCompare(String(right.email || "")) ||
  memberId(left) - memberId(right);

const toUtcIso = (date, time, zone) => {
  if (!date || !time) return null;
  const dt = DateTime.fromISO(`${date}T${time}`, { zone: zone || "UTC" });
  return dt.isValid ? dt.toUTC().toISO() : null;
};

const selectedValues = (event) =>
  Array.from(event.target.selectedOptions || []).map((option) => Number(option.value));

const normalizeIds = (values) =>
  Array.from(new Set((values || []).map(Number).filter(Number.isFinite)));

const getRequestedIds = (schedule) => {
  if (Array.isArray(schedule?.requested_user_ids)) {
    return normalizeIds(schedule.requested_user_ids);
  }
  return normalizeIds(
    (schedule?.requested_users || []).map((participant) => participant.user_id),
  );
};

const EMPTY = {
  requested_user_ids: [],
  cc_user_ids: [],
  inspection_id: "",
  meeting_type: "site_walk",
  title: "",
  location: "",
  schedule_date: "",
  start_time: "",
  end_time: "",
  notes: "",
};

export default function WalkScheduleEditModal({
  open,
  onClose,
  projectId,
  projectTimezone,
  schedule,
  team = [],
  inspections = [],
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const creatorId = Number(schedule?.requested_by_user_id);
  const teamOptions = useMemo(
    () =>
      (team || [])
        .filter((member) => member?.is_active !== false)
        .filter((member) => memberId(member) !== creatorId)
        .sort(compareMembers),
    [creatorId, team],
  );

  const requestedSet = useMemo(
    () => new Set((form.requested_user_ids || []).map(Number)),
    [form.requested_user_ids],
  );
  const ccOptions = useMemo(
    () => teamOptions.filter((member) => !requestedSet.has(memberId(member))),
    [requestedSet, teamOptions],
  );

  useEffect(() => {
    if (!open || !schedule) return;
    const start = toLocalFields(schedule.preferred_start_at, projectTimezone);
    const end = toLocalFields(schedule.preferred_end_at, projectTimezone);
    setForm({
      requested_user_ids: getRequestedIds(schedule),
      cc_user_ids: normalizeIds(schedule.cc_user_ids),
      inspection_id: schedule.inspection_id == null ? "" : String(schedule.inspection_id),
      meeting_type: schedule.meeting_type || "site_walk",
      title: schedule.title || "",
      location: schedule.location || "",
      schedule_date: start.date,
      start_time: start.time,
      end_time: end.time,
      notes: schedule.notes || "",
    });
    setError("");
    setSaving(false);
  }, [open, projectTimezone, schedule]);

  if (!open || !schedule) return null;

  const setField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    const title = form.title.trim();
    const requestedIds = normalizeIds(form.requested_user_ids);
    const ccIds = normalizeIds(form.cc_user_ids).filter(
      (id) => !requestedIds.includes(id),
    );
    const startIso = toUtcIso(
      form.schedule_date,
      form.start_time,
      projectTimezone,
    );
    const endIso = toUtcIso(
      form.schedule_date,
      form.end_time,
      projectTimezone,
    );

    if (!title) {
      setError("Title is required.");
      return;
    }
    if (!requestedIds.length) {
      setError("Select at least one requested participant.");
      return;
    }
    if (
      !startIso ||
      !endIso ||
      DateTime.fromISO(endIso) <= DateTime.fromISO(startIso)
    ) {
      setError("Choose a valid date and From/To time.");
      return;
    }

    setSaving(true);
    try {
      const availability = await walkSchedulesAPI.checkAvailability(projectId, {
        user_ids: [creatorId, ...requestedIds].filter(Number.isFinite),
        cc_user_ids: ccIds,
        start_at: startIso,
        end_at: endIso,
        exclude_schedule_id: schedule.id,
      });
      const conflicts = availability?.data?.hard_conflicts || [];
      const warnings = availability?.data?.warnings || [];
      if (conflicts.length) {
        setError(
          availability?.data?.message ||
            "The edited time overlaps an accepted walk or busy period.",
        );
        return;
      }
      if (
        warnings.length &&
        !window.confirm(
          "Some participants have pending schedule conflicts. Save the edited request anyway?",
        )
      ) {
        return;
      }

      const response = await walkSchedulesAPI.update(projectId, schedule.id, {
        requested_user_ids: requestedIds,
        cc_user_ids: ccIds,
        inspection_id: form.inspection_id ? Number(form.inspection_id) : null,
        meeting_type: form.meeting_type,
        title,
        location: form.location.trim() || null,
        notes: form.notes.trim() || null,
        preferred_start_at: startIso,
        preferred_end_at: endIso,
      });
      await onSaved?.(response?.data || null);
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || requestError?.message || "Could not update the meeting request.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-white px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-950">Edit meeting request</h3>
            <p className="mt-1 text-xs text-gray-500">
              The creator may correct details. Changing the time or requested participants resets their responses.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 p-5">
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Requested participants *
              <select
                multiple
                size={6}
                value={form.requested_user_ids.map(String)}
                onChange={(event) => {
                  const ids = selectedValues(event);
                  setForm((current) => ({
                    ...current,
                    requested_user_ids: ids,
                    cc_user_ids: current.cc_user_ids.filter((id) => !ids.includes(Number(id))),
                  }));
                }}
                className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                {teamOptions.map((member) => (
                  <option key={memberId(member)} value={memberId(member)}>
                    {memberName(member)}{member.role ? ` · ${roleLabel(member.role)}` : ""}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs font-normal text-gray-400">
                Hold Ctrl/Cmd to select multiple people.
              </span>
            </label>

            <label className="text-sm font-medium text-gray-700">
              CC participants
              <select
                multiple
                size={6}
                value={form.cc_user_ids.map(String)}
                onChange={(event) => setField("cc_user_ids", selectedValues(event))}
                className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                {ccOptions.map((member) => (
                  <option key={memberId(member)} value={memberId(member)}>
                    {memberName(member)}{member.role ? ` · ${roleLabel(member.role)}` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Meeting type
              <select
                value={form.meeting_type}
                onChange={(event) => setField("meeting_type", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm"
              >
                {MEETING_TYPES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700">
              Related inspection
              <select
                value={form.inspection_id}
                onChange={(event) => setField("inspection_id", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm"
              >
                <option value="">None</option>
                {(inspections || []).map((inspection) => (
                  <option key={inspection.id} value={inspection.id}>
                    {inspection.inspection_number || `Inspection #${inspection.id}`}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            Title *
            <input
              value={form.title}
              onChange={(event) => setField("title", event.target.value)}
              maxLength={255}
              className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
            />
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Location <span className="font-normal text-gray-400">(optional)</span>
            <input
              value={form.location}
              onChange={(event) => setField("location", event.target.value)}
              maxLength={500}
              placeholder="Conference room, trailer, building/level, or online link"
              className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
            />
          </label>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-sm font-medium text-gray-700">
              Date *
              <input
                type="date"
                value={form.schedule_date}
                onChange={(event) => setField("schedule_date", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              From *
              <input
                type="time"
                value={form.start_time}
                onChange={(event) => setField("start_time", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              To *
              <input
                type="time"
                value={form.end_time}
                onChange={(event) => setField("end_time", event.target.value)}
                className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
              />
            </label>
          </div>

          <label className="block text-sm font-medium text-gray-700">
            Notes
            <textarea
              rows={4}
              value={form.notes}
              onChange={(event) => setField("notes", event.target.value)}
              className="mt-1.5 w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm"
            />
          </label>

          <div className="flex justify-end gap-2 border-t pt-4">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border px-4 py-2 text-sm font-medium">
              Cancel
            </button>
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
